import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

// 格式化金额
export const formatAmount = (amount, currency = '¥') => {
  if (amount === null || amount === undefined) return '-';
  return `${Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// 货币转换（从基础货币转换到目标货币）
export const convertCurrency = (amount, exchangeRate = 1) => {
  if (amount === null || amount === undefined) return 0;
  return Number(amount) * Number(exchangeRate);
};

// 格式化带地区的金额（自动转换汇率）
export const formatAmountWithRegion = (amount, region) => {
  if (amount === null || amount === undefined) return '-';
  if (!region) return formatAmount(amount);
  
  const convertedAmount = convertCurrency(amount, region.exchange_rate || region.exchangeRate || 1);
  const currencySymbol = region.currency_symbol || region.currencySymbol || '¥';
  
  return `${currencySymbol}${Number(convertedAmount).toLocaleString('zh-CN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

// 格式化数字
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('zh-CN');
};

// 格式化百分比
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';
  return `${(Number(value) * 100).toFixed(decimals)}%`;
};

// 格式化日期时间（从数据库UTC时间转换为本地时间）
export const formatDateTime = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '-';
  return dayjs.utc(date).local().format(format);
};

// 格式化日期（从数据库UTC时间转换为本地时间）
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  if (!date) return '-';
  return dayjs.utc(date).local().format(format);
};

// 格式化时间（从数据库UTC时间转换为本地时间）
export const formatTime = (date, format = 'HH:mm:ss') => {
  if (!date) return '-';
  return dayjs.utc(date).local().format(format);
};

// 相对时间（友好显示）
export const formatRelativeTime = (date) => {
  if (!date) return '-';
  const now = dayjs();
  const target = dayjs(date);
  const diffInSeconds = now.diff(target, 'second');
  
  // 1分钟内
  if (diffInSeconds < 60) {
    return '刚刚';
  }
  // 1小时内
  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)}分钟前`;
  }
  // 24小时内
  if (diffInSeconds < 86400) {
    return `${Math.floor(diffInSeconds / 3600)}小时前`;
  }
  // 7天内
  if (diffInSeconds < 604800) {
    return `${Math.floor(diffInSeconds / 86400)}天前`;
  }
  // 超过7天显示完整日期
  return target.format('YYYY-MM-DD HH:mm');
};

// 手机号脱敏
export const maskPhone = (phone) => {
  if (!phone) return '-';
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
};

// 身份证脱敏
export const maskIdCard = (idCard) => {
  if (!idCard) return '-';
  return idCard.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2');
};

// 银行卡脱敏
export const maskBankCard = (cardNumber) => {
  if (!cardNumber) return '-';
  return cardNumber.replace(/(\d{4})\d{8,12}(\d{4})/, '$1****$2');
};

// 邮箱脱敏
export const maskEmail = (email) => {
  if (!email) return '-';
  const [username, domain] = email.split('@');
  if (username.length <= 2) return email;
  return `${username.substring(0, 2)}***@${domain}`;
};

// 获取状态标签颜色
export const getStatusColor = (status) => {
  const statusMap = {
    // 数字状态（用户状态）
    0: 'error',      // 禁用
    1: 'success',    // 正常
    2: 'warning',    // 冻结
    // 字符串数字状态（用户状态）
    '0': 'error',      // 禁用
    '1': 'success',    // 正常
    '2': 'warning',    // 冻结
    // 英文状态（用户状态）
    'active': 'success',    // 正常
    'inactive': 'error',    // 禁用
    'disabled': 'error',    // 禁用
    'frozen': 'warning',    // 冻结
    'normal': 'success',    // 正常
    // 产品状态
    paused: 'warning',
    // 订单状态（新的统一状态机）
    'pending': 'processing',              // 待支付
    'paid': 'success',                     // 已支付
    'processing': 'processing',            // 处理中
    'holding': 'success',                  // 持有中
    'matured': 'warning',                 // 已到期
    'settlement_processing': 'processing', // 清算中
    'completed': 'success',                // 已完成
    'cancelled': 'default',                // 已取消
    'failed': 'error',                     // 失败
    // 其他状态
    success: 'success',
    error: 'error',
    online: 'success',
    offline: 'default',
  };
  return statusMap[status] || 'default';
};

// 获取状态文本
export const getStatusText = (status) => {
  const statusMap = {
    // 数字状态（用户状态）
    0: '禁用',
    1: '正常',
    2: '冻结',
    // 字符串数字状态（用户状态）
    '0': '禁用',
    '1': '正常',
    '2': '冻结',
    // 英文状态（用户状态）
    'active': '正常',
    'inactive': '禁用',
    'disabled': '禁用',
    'frozen': '冻结',
    'normal': '正常',
    // 产品状态
    paused: '暂停交易',
    // 订单状态（新的统一状态机）
    'pending': '待支付',
    'paid': '已支付',
    'processing': '处理中',
    'holding': '持有中',
    'matured': '已到期',
    'settlement_processing': '清算中',
    'completed': '已完成',
    'cancelled': '已取消',
    'failed': '失败',
    // 其他状态
    success: '成功',
    error: '失败',
    online: '在线',
    offline: '离线',
  };
  return statusMap[status] || status;
};

// 防抖函数
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 节流函数
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// 深拷贝
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

// 生成随机字符串
export const generateRandomString = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// 验证手机号
export const validatePhone = (phone) => {
  const phoneRegex = /^1[3-9]\d{9}$/;
  return phoneRegex.test(phone);
};

// 验证邮箱
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// 验证身份证
export const validateIdCard = (idCard) => {
  const idCardRegex = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/;
  return idCardRegex.test(idCard);
};

// 获取文件扩展名
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

// 格式化文件大小
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 下载文件
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 复制到剪贴板
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // 降级方案
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

// 计算看板统计所需的时区时间范围（按浏览器本地时区）
// 返回各区间的 UTC 毫秒时间戳（dayjs().valueOf() 即为 UTC 毫秒，与时区无关）
export const getDashboardTimeRange = () => {
  const now = dayjs();
  // 今日：[本地今天0点, 明天0点)
  const todayStart = now.startOf('day');
  const todayEnd = todayStart.add(1, 'day');
  // 本周起点（默认从周日0点起算）
  const weekStart = now.startOf('week');
  // 本月起点
  const monthStart = now.startOf('month');

  return {
    today: { startTs: todayStart.valueOf(), endTs: todayEnd.valueOf() },
    week: { startTs: weekStart.valueOf() },
    month: { startTs: monthStart.valueOf() }
  };
};

